import { validRepeaterElements } from "@builder/store/slices/BuilderSlice/builderMap";
import { BuildComponentKey, BuildNode } from "@common/build/types";
import { applyInlineStylesOnNode } from "./jsxStyleHelper";

import { DynamicData } from "@common/build/dynamicData";
import { CommonPageType } from "./architecture/pagesAndComponentsInfo";
import { DYNAMIC_GRIDS_PROP_MAPPINGS, validateDynamicGridQuery } from "./jsxDynamicGrids";
import {
    setValueAtPath
} from "./jsxHelpers";

export interface PropMapping {
    nodePath: string | (string | number)[]; // Accept dot-notation string or array path
    jsxProp: string;
    customAddJSXAttToNode?: (node: BuildNode, value: any) => void;
}

// todo: change to obj so could be overridden in the function?
export const DEFAULT_PROP_MAPPINGS: PropMapping[] = [
    { nodePath: ["text"], jsxProp: "text" },
    {
        nodePath: ["dynamicData"],
        jsxProp: "dynamicData",
        customAddJSXAttToNode: (node: BuildNode, value: any) => {
            // if value is an object, use the key as the value
            if (typeof value !== "string") {
                console.warn("dynamicData is NOT a string !: ", value);
                return;
            }
            if (value in DynamicData) {
                setValueAtPath(node, ["dynamicData"], value);
            } else {
                console.warn(`Dynamic data key ${value} not found`);
            }
        }
    },
    // { nodePath: ["type"], jsxProp: "type" },
    { nodePath: ["input", "placeholder"], jsxProp: "placeholder" },
    {
        nodePath: ["attr", "style"],
        jsxProp: "style",
        customAddJSXAttToNode: (node: BuildNode, value: any) => {
            applyInlineStylesOnNode(value, node);
        }
    },
    { nodePath: ["image"], jsxProp: "image" },
    { nodePath: ["prepend"], jsxProp: "prepend" },
    { nodePath: ["append"], jsxProp: "append" },
    { nodePath: ["customName"], jsxProp: "customName" },
    { nodePath: ["timeFormat"], jsxProp: "timeFormat" },
    { nodePath: ["customCSS"], jsxProp: "customCSS" }
];

export interface JSXNodeMappingSettings {
    ignoreJSXChildren?: boolean;
    allowedChildNames?: string[];
    additionalPropMappings?: PropMapping[];
    customValidate?: (node: BuildNode, nodeMap: Record<string, BuildNode>) => void;
    extraInstructions?: string;
    isTextable?: boolean;
    typicallyUsedOnPage?: CommonPageType[];
}

export const JSX_NODE_MAPPINGS: Partial<Record<BuildComponentKey, JSXNodeMappingSettings>> = {
    baseLayerPage: {},
    baseLayerPopup: {},
    baseLayerCFD: {},
    div: {},
    button: {
        isTextable: true
    },
    image: {
        additionalPropMappings: [
            { nodePath: ["image", "url"], jsxProp: "src" },
            { nodePath: ["image", "name"], jsxProp: "alt" }
        ]
    },
    productSection: {
        typicallyUsedOnPage: ["product-page"]
    },
    customerSection: {
        typicallyUsedOnPage: ["customer-page"]
    },
    orderSection: {
        typicallyUsedOnPage: ["orders-page"]
    },
    productList: {
        typicallyUsedOnPage: ["product-page"],
        additionalPropMappings: DYNAMIC_GRIDS_PROP_MAPPINGS,
        extraInstructions: "DON'T MODIFY ANY ELEMENT INSIDE THIS PRODUCT LIST YOURSELF. MUST CALL editOrCreateDynamicGrid FOR ANY CHANGES. To edit or create a product list, you MUST use the editOrCreateDynamicGrid tool",
        customValidate: (node, nodeMap) => {
            if (node.query) {
                validateDynamicGridQuery(node.query);
            }
            const hasRepeater = (node.children || []).some(id => nodeMap[id]?.name === "repeaterDiv");
            if (!hasRepeater) {
                throw new Error("productList must include a repeaterDiv child");
            }
        }
    },
    productGrid: {
        typicallyUsedOnPage: ["product-page"],
        additionalPropMappings: DYNAMIC_GRIDS_PROP_MAPPINGS,
        extraInstructions: "DON'T MODIFY ANY ELEMENT INSIDE THIS PRODUCT GRID YOURSELF. MUST CALL editOrCreateDynamicGrid FOR ANY CHANGES.  To edit or create a product grid, you MUST use the editOrCreateDynamicGrid tool",
        customValidate: (node, nodeMap) => {
            if (node.query) {
                validateDynamicGridQuery(node.query);
            }
            const hasRepeater = (node.children || []).some(id => nodeMap[id]?.name === "repeaterDiv");
            if (!hasRepeater) {
                throw new Error("productGrid must include a repeaterDiv child");
            }
        }
    },
    customerList: {
        typicallyUsedOnPage: ["customer-page"],
        additionalPropMappings: DYNAMIC_GRIDS_PROP_MAPPINGS,
        extraInstructions: "DON'T MODIFY ANY ELEMENT INSIDE THIS CUSTOMER LIST YOURSELF. MUST CALL editOrCreateDynamicGrid FOR ANY CHANGES. To edit or create a customer list, you MUST use the editOrCreateDynamicGrid tool",
        customValidate: (node, nodeMap) => {
            if (node.query) {
                validateDynamicGridQuery(node.query);
            }
            const hasRepeater = (node.children || []).some(id => nodeMap[id]?.name === "repeaterDiv");
            if (!hasRepeater) {
                throw new Error("customerList must include a repeaterDiv child");
            }
        }
    },
    repeaterDiv: {
        allowedChildNames: validRepeaterElements,
        extraInstructions: "DON'T MODIFY THIS REPEATER DIV OR ANY OF ITS CHILDREN YOURSELF. MUST CALL editOrCreateDynamicGrid FOR ANY CHANGES, to inject THE WHOLE GRID / KIST",
        customValidate: (node, nodeMap) => {
            if (!node.parent) {
                throw new Error("repeaterDiv must have a parent");
            }
            const parent = nodeMap[node.parent];
            if (!parent) {
                throw new Error("When handling a repeaterDiv, you must that you use the editOrCreateDynamicGrid tool to edit the grid/list and that it's inside a dynamic grid/list");
            }
            if (!parent.type) {
                console.error("repeaterDiv parent has no type", parent);
                throw new Error("make sure you make all changes to the Grid and not directly to the repeater Div");
            }
            if (parent?.type !== "repeater" && !parent.name.toLowerCase().includes("list") && !parent.name.toLowerCase().includes("grid")) {
                console.log("repeaterDiv parent", parent);
                throw new Error(`repeaterDiv must be a direct child of a dynamic grid/list component, current parent type is ${parent.type}`);
            }
        }
    },
    text: {
        isTextable: true
    },
    cashButton: {
        typicallyUsedOnPage: ["checkout-page"],
        isTextable: true
    },
    customerNotes: {
        typicallyUsedOnPage: ["customer-page"]
    },
    divider: {},
    menu: {},
    productQuantity: {
        typicallyUsedOnPage: ["product-page"]
    },
    productVariations: {
        typicallyUsedOnPage: ["product-page"]
    },
};
